import { selectDiscounts } from '@/redux/data/dataSlice';
import { DiscountType } from '@/redux/data/dataTypes';
import { useAppSelector } from '@/redux/hooks';
import { InboxOutlined } from '@ant-design/icons';
import Fuse from 'fuse.js';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import DiscountItem from './DiscountItem';

interface DiscountListProps {
  searchTxt?: string;
  listContentClassName?: string;
  onItemPress?: (item: DiscountType) => void;
  layout?: 'grid' | 'compact';
}

const DiscountList: FC<DiscountListProps> = ({
  searchTxt,
  listContentClassName = '',
  onItemPress,
  layout = 'grid',
}) => {
  const { t } = useTranslation();
  const discounts = useAppSelector(selectDiscounts);

  const filteredList = useMemo(() => {
    if (searchTxt && searchTxt.length >= 3) {
      const fuse = new Fuse(discounts, {
        includeScore: false,
        isCaseSensitive: false,
        threshold: 0.3,
        keys: ['name'],
      });
      return fuse.search(searchTxt).map((d) => d.item);
    }
    return discounts;
  }, [discounts, searchTxt]);

  if (filteredList.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-6 bg-[#f6f8fc] dark:bg-gray-700 rounded-xl">
        <div className="flex flex-col items-center p-5 text-center bg-white dark:bg-gray-600 rounded-xl shadow-sm max-w-sm w-full">
          <div className="flex items-center justify-center w-14 h-14 mb-3 rounded-full bg-[#e6f7ff] dark:bg-gray-500 text-frenchBlue dark:text-white">
            <InboxOutlined className="text-xl" />
          </div>
          <div className="mb-1 text-base font-semibold text-gray-800 dark:text-white">
            {t('common:no_discounts_found')}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-300">
            {t('common:try_adjusting_search', 'Try adjusting your search')}
          </div>
        </div>
      </div>
    );
  }

  const containerClasses =
    layout === 'compact'
      ? `flex flex-wrap gap-1.5 ${listContentClassName}`
      : `grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2.5 ${listContentClassName}`;

  return (
    <div className="bg-[#f6f8fc] dark:bg-gray-700 rounded-xl p-1.5">
      <div className={containerClasses}>
        {filteredList.map((discount) => (
          <DiscountItem
            key={discount.ID}
            discount={discount}
            onItemPress={onItemPress}
            layout={layout}
          />
        ))}
      </div>
    </div>
  );
};

export default DiscountList;
